import { useMemo } from 'react';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { FilterOption } from '@/api';

/**
 * The paging every filter-option read shares: same page size, same
 * de-duplication, same "keep the old list on screen while the new search
 * loads". Three endpoints answer with this shape, so the machinery lives here
 * rather than three times over.
 */

export const FILTER_OPTIONS_PAGE_SIZE = 25;

export type FetchFilterOptionsQuery = {
    companyId: string;
    search: string;
    // Only fetch once the filter popup has been opened.
    enabled: boolean;
};

export type FetchScopedFilterOptionsQuery = FetchFilterOptionsQuery & {
    // Restrict user/device options to these teams.
    teamIds: string[];
};

export type FetchFilterOptionsResult<T> = {
    options: T[];
    total: number;
    status: 'error' | 'success' | 'pending';
    fetchNextPage: () => void;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    refetch: () => void;
};

export function useFetchFilterOptionsPages<T extends FilterOption>(
    queryKey: unknown[],
    enabled: boolean,
    fetchPage: (offset: number) => Promise<{
        data: T[];
        total: number;
        hasMore: boolean;
    }>
): FetchFilterOptionsResult<T> {
    const {
        data,
        status,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        refetch
    } = useInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam }) =>
            fetchPage(pageParam * FILTER_OPTIONS_PAGE_SIZE),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.hasMore ? allPages.length : undefined;
        },
        // Keep showing the previous list while a new search term loads,
        // instead of flashing empty.
        placeholderData: keepPreviousData,
        enabled
    });

    const options = useMemo(() => {
        const map = new Map<string, T>();
        data?.pages.forEach((page) => {
            page.data.forEach((option) => {
                map.set(option.id, option);
            });
        });
        return [...map.values()];
    }, [data]);

    const total = useMemo(() => {
        const pages = data?.pages;
        return pages && pages.length > 0 ? pages[pages.length - 1].total : 0;
    }, [data]);

    return {
        options,
        total,
        status,
        fetchNextPage: () => {
            void fetchNextPage();
        },
        isFetchingNextPage,
        hasNextPage,
        refetch: () => {
            void refetch();
        }
    };
}
